import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { connectSocket, getSocket } from '../services/socket';
import CodeEditorWithHighlight from './CodeEditorWithHighlight';
import Chat from './Chat';
import UsersList from './UsersList';
import NamePrompt from './NamePrompt';

function Editor() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [userCount, setUserCount] = useState(1);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [language, setLanguage] = useState('javascript');
  const [theme, setTheme] = useState('dark');
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [users, setUsers] = useState([]);
  const [showNamePrompt, setShowNamePrompt] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);

  const handleNameSubmit = (userName) => {
    setShowNamePrompt(false);
    const socket = connectSocket();
    setCurrentUserId(socket.id);
    
    const handleConnect = () => {
      console.log('Socket connected, joining room:', roomId);
      socket.emit('join-room', { roomId, userName });
    };

    if (socket.connected) {
      handleConnect();
    } else {
      socket.on('connect', () => {
        setCurrentUserId(socket.id);
        handleConnect();
      });
    }

    socket.on('load-code', (loadedCode) => {
      console.log('Received code:', loadedCode);
      setCode(loadedCode);
      setIsConnected(true);
      setIsLoading(false);
    });

    socket.on('load-chat', (messages) => {
      setChatMessages(messages);
    });

    socket.on('users-update', (usersList) => {
      setUsers(usersList);
      setUserCount(usersList.length);
    });

    socket.on('user-joined', (data) => {
      console.log('User joined:', data);
      setUserCount(data.userCount);
      toast.success(`${data.userName} joined the room!`);
    });

    socket.on('user-left', (data) => {
      console.log('User left:', data);
      setUserCount(data.userCount);
      toast('Someone left the room', { icon: '👋' });
    });

    socket.on('code-update', (data) => {
      console.log('Code update received:', data);
      setCode(data.code);
    });

    socket.on('chat-message', (message) => {
      setChatMessages(prev => [...prev, message]);
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
      toast.error('Disconnected from server');
    });

    return () => {
      socket.off('connect');
      socket.off('load-code');
      socket.off('load-chat');
      socket.off('users-update');
      socket.off('user-joined');
      socket.off('user-left');
      socket.off('code-update');
      socket.off('chat-message');
      socket.off('disconnect');
    };
  };

  const handleCodeChange = (newCode) => {
    setCode(newCode);
    
    const socket = getSocket();
    socket.emit('code-change', {
      roomId,
      code: newCode,
      cursorPosition: { line: 0, column: 0 }
    });
  };

  const handleSendMessage = (message) => {
    const socket = getSocket();
    socket.emit('chat-message', { roomId, message });
  };

  const copyRoomLink = () => {
    const roomLink = window.location.href;
    navigator.clipboard.writeText(roomLink)
      .then(() => {
        toast.success('Room link copied to clipboard!');
      })
      .catch(() => {
        toast.error('Failed to copy link');
      });
  };

  const copyCode = () => {
    navigator.clipboard.writeText(code)
      .then(() => {
        toast.success('Code copied to clipboard!');
      })
      .catch(() => {
        toast.error('Failed to copy code');
      });
  };

  const downloadCode = () => {
    const fileExtensions = {
      javascript: 'js',
      python: 'py',
      java: 'java',
      cpp: 'cpp',
      html: 'html',
      css: 'css',
      typescript: 'ts',
      json: 'json',
    };
    
    const extension = fileExtensions[language] || 'txt';
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `code-${roomId}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Code downloaded!');
  };

  const clearEditor = () => {
    if (window.confirm('Are you sure you want to clear the editor? This will affect all users in the room.')) {
      setCode('');
      const socket = getSocket();
      socket.emit('code-change', {
        roomId,
        code: '',
        cursorPosition: { line: 0, column: 0 }
      });
      toast.success('Editor cleared!');
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
    toast.success(`Switched to ${theme === 'dark' ? 'light' : 'dark'} theme`);
  };

  const leaveRoom = () => {
    navigate('/');
  };

  const getStats = () => {
    const lines = code.split('\n').length;
    const chars = code.length;
    return { lines, chars };
  };

  const stats = getStats();

  if (showNamePrompt) {
    return <NamePrompt onSubmit={handleNameSubmit} />;
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Connecting to room...</p>
      </div>
    );
  }

  return (
    <div className={`editor-page ${theme}`}>
      <div className={`editor-container ${theme}`}>
        <div className="editor-header">
          <div className="room-info">
            <span className="room-id">Room: {roomId}</span>
            <span className="user-count">👥 {userCount} online</span>
            <span className={`status ${isConnected ? 'connected' : 'disconnected'}`}>
              {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
            </span>
          </div>
          
          <div className="editor-controls">
            <select 
              value={language} 
              onChange={(e) => setLanguage(e.target.value)}
              className="language-select"
            >
              <option value="javascript">JavaScript</option>
              <option value="python">Python</option>
              <option value="java">Java</option>
              <option value="cpp">C++</option>
              <option value="html">HTML</option>
              <option value="css">CSS</option>
              <option value="typescript">TypeScript</option>
              <option value="json">JSON</option>
            </select>
            
            <button onClick={toggleTheme} className="control-btn" title="Toggle theme">
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <button onClick={copyRoomLink} className="control-btn" title="Copy room link">
              🔗 Share
            </button>
            <button onClick={copyCode} className="control-btn" title="Copy code">
              📋 Copy
            </button>
            <button onClick={downloadCode} className="control-btn" title="Download code">
              💾 Download
            </button>
            <button onClick={clearEditor} className="control-btn danger" title="Clear editor">
              🗑️ Clear
            </button>
            <button onClick={leaveRoom} className="control-btn" title="Leave room">
              🚪 Leave
            </button>
          </div>
        </div>
        
        <div className="editor-main">
          <div className="editor-sidebar">
            <UsersList users={users} currentUserId={currentUserId} />
          </div>
          
          <CodeEditorWithHighlight 
            value={code}
            onChange={handleCodeChange}
            language={language}
            theme={theme}
          />
        </div>
        
        <div className="editor-footer">
          <span>Lines: {stats.lines}</span>
          <span>Characters: {stats.chars}</span>
          <span>Language: {language.toUpperCase()}</span>
        </div>
      </div>
      
      <Chat 
        messages={chatMessages}
        onSendMessage={handleSendMessage}
        isOpen={isChatOpen}
        onToggle={() => setIsChatOpen(!isChatOpen)}
      />
    </div>
  );
}

export default Editor;